import { BpButton } from '../../../../shared/components/buttons/bp-button/BpButton'
import { IconPlus } from '../../../../shared/components/icons/IconPlus'
import React, { useMemo } from 'react'
import { Square } from '../../../../shared/components/utility/square/Square'
import { DropOptions } from '../../../../shared/components/forms/drop-options/DropOptions'
import { useAppDispatch } from '../../../../app/store/hooks/Redux'
import { setModal } from '../../../../app/store/reducers/AppSlice'
import {
  TrackingParamConfig,
  useGetAffiliateRuleListQuery,
  usePostAffiliateRuleDeleteAffiliatesMutation,
  usePostAffiliateRuleRunSyncMutation,
  usePostAffiliateRuleUpdateMutation,
} from '../../../../app/api'
import { Link, useParams } from 'react-router-dom'
import { UpdateDomains } from './UpdateDomains'
import { UpdateParams } from './UpdateParams'
import { Preloader } from '../../../../shared/components/cards/preloader/Preloader'
import { Tooltip } from '../../../../shared/components/text/tooltip/Tooltip'
import { useTranslation } from 'react-i18next'
import { renderPathBasedRoles } from './lib/PathBasedUrl'
import clsx from 'clsx'
import styles from './ProjectAffiliateRule.module.scss'
import { AffiliateTrackingStub } from '../../../../lib/widgets/stubs/AffiliateTrackingStub'
import { StubLayout } from '../../../../lib/widgets/stubs/StubLayout'
import { useCreateAffiliateRuleModal } from './useCreateAffiliateRuleModal'

interface Props {
  id: number
  enabled: boolean
  aff_count_30d: number
  domain: string
  main_domain_zone: string
  domain_zones: string[]
  url: string
  params: TrackingParamConfig[]
  is_path_based?: boolean
  is_syncing: boolean
  index: number
}

const Rule = React.memo((props: Props) => {
  const { t } = useTranslation()
  const { id } = useParams()
  const dispatch = useAppDispatch()

  const [updateStatus] = usePostAffiliateRuleUpdateMutation()
  const [runSync, { isLoading: runSyncLoading, isUninitialized }] =
    usePostAffiliateRuleRunSyncMutation()
  const [deleteAffiliates, { isLoading: deleteAffiliatesLoading }] =
    usePostAffiliateRuleDeleteAffiliatesMutation()

  return (
    <Square className={styles.square}>
      <div className={styles.ruleHeader}>
        <div className={styles.ruleTitleRow}>
          <h2 className={styles.ruleTitle}>
            #{props.index} {t('pages.project.trackingRule')}
          </h2>
          <div className={styles.badges}>
            <DropOptions
              options={[
                {
                  text: t('pages.project.activate'),
                  value: 'true',
                  hidden: props.enabled,
                },
                {
                  text: t('pages.project.deactivate'),
                  value: 'false',
                  hidden: !props.enabled,
                },
              ]}
              onChange={(e) => {
                updateStatus({
                  body: {
                    scenario: 'update_status',
                    id: props.id,
                    project_id: Number(id),
                    enabled: e === 'true',
                  },
                })
              }}
            >
              {props.enabled ? (
                <span className={styles.badgeActive}>{t('pages.project.active')}</span>
              ) : (
                <span className={styles.badgeInactive}>{t('pages.project.inactive')}</span>
              )}
            </DropOptions>
            {props.is_syncing && (
              <Tooltip
                overlayClassName={'max-w-full'}
                title={'Data will be updated within ~5 minutes'}
              >
                <div className={styles.badgeWrap}>
                  <span className={styles.badgeSyncing}>Updating affiliate</span>
                </div>
              </Tooltip>
            )}
          </div>
        </div>
        <Link
          className={clsx(
            styles.affiliatesLink,
            !props.aff_count_30d && styles.affiliatesLinkDisabled,
          )}
          to={`/ppc-compliance?project=${id}&affiliateRule=${props.id}`}
        >
          {props.aff_count_30d} {t('pages.project.affiliatesFound')}
        </Link>
      </div>
      <div className={styles.cardsRow}>
        <div className={styles.domainCard}>
          <p className={styles.domainCardText}>
            <span className={styles.domainCardBold}>{t('pages.project.domains')}</span>{' '}
            <Tooltip overlayClassName='max-w-[350px] break-all' title={props.url}>
              <span className={styles.domainCardHover}>
                {props.domain}
                {props.main_domain_zone}
              </span>
            </Tooltip>
            {props.domain_zones.length > 1 && ', '}
            {props.domain_zones.filter((domain) => domain !== props.main_domain_zone).join(', ')}
          </p>
          <BpButton
            className={styles.editButtonFull}
            size={'sm'}
            theme={'outline'}
            onClick={() =>
              dispatch(
                setModal({
                  children: <UpdateDomains {...props} />,
                  size: 'lg',
                }),
              )
            }
          >
            {t('buttons.edit')}
          </BpButton>
        </div>
        <div className={styles.paramsCard}>
          <p className={clsx(styles.paramsCardText, props.is_path_based && 'break-all')}>
            <span className={styles.domainCardBold}>{t('pages.project.parameters')}</span>{' '}
            {props.is_path_based
              ? renderPathBasedRoles(
                  props.url,
                  (segment) => props.params.find((param) => param.value === segment)?.type,
                )
              : props.params.map((param) => param.value).join(', ')}
          </p>
          <BpButton
            className={styles.editButtonFull}
            size={'sm'}
            theme={'outline'}
            onClick={() =>
              dispatch(
                setModal({
                  children: <UpdateParams {...props} />,
                  size: 'lg',
                }),
              )
            }
          >
            {t('buttons.edit')}
          </BpButton>
        </div>
      </div>
      <div className={styles.actionsRow}>
        <Tooltip
          title={
            props.is_syncing || !isUninitialized
              ? t('pages.project.waitBeforeUpdate')
              : t('pages.project.updateInfo')
          }
          disabled={!props.aff_count_30d}
        >
          <BpButton
            className={styles.actionButton}
            disabled={props.is_syncing || !isUninitialized}
            loading={runSyncLoading}
            size={'sm'}
            theme={'outline'}
            type='button'
            onClick={() => runSync({ body: { id: props.id, project_id: Number(id) } })}
          >
            {t('pages.project.updateAffiliates')}
          </BpButton>
        </Tooltip>
        <Tooltip title={t('pages.project.deleteNotice')}>
          <BpButton
            className={styles.actionButton}
            disabled={!props.aff_count_30d}
            loading={deleteAffiliatesLoading}
            size={'sm'}
            theme={'outline'}
            type='button'
            onClick={() => deleteAffiliates({ body: { id: props.id, project_id: Number(id) } })}
          >
            {t('pages.project.deleteAffiliates')}
          </BpButton>
        </Tooltip>
      </div>
    </Square>
  )
})

export const ProjectAffiliateRule = () => {
  const { t } = useTranslation()
  const { id } = useParams()
  const handleCreateAffiliateRuleModal = useCreateAffiliateRuleModal()

  const {
    data: rules,
    isLoading,
    isFetching,
  } = useGetAffiliateRuleListQuery({ projectId: Number(id) })

  const sortedData = useMemo(() => {
    if (!rules?.data) return []

    const items = [...(rules.data.items ?? [])]

    return items.sort((a, b) => (!a.enabled ? 1 : 0) - (!b.enabled ? 1 : 0))
  }, [rules])

  if (isLoading || isFetching) {
    return <Preloader />
  }

  return (
    <div className={styles.root}>
      {!!sortedData.length && (
        <div className={styles.topRow}>
          <p className={styles.description}>{t('pages.project.trackingDescription')}</p>
          <div className={styles.addButtonWrap}>
            <BpButton
              className={styles.addButton}
              iconLeft={sortedData.length ? <IconPlus /> : undefined}
              theme={'default'}
              onClick={handleCreateAffiliateRuleModal}
            >
              {sortedData.length ? t('pages.project.addRule') : t('pages.project.setUpTracking')}
            </BpButton>
          </div>
        </div>
      )}
      {sortedData.length ? (
        <div className={styles.rulesGrid}>
          {sortedData.map((rule, index) => (
            <Rule key={index} index={++index} {...rule} />
          ))}
        </div>
      ) : (
        <StubLayout gap={24}>
          <AffiliateTrackingStub project={id} />
        </StubLayout>
      )}
    </div>
  )
}
